import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { BehaviorSubject, Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { DeliveryResponseDto } from 'src/generated_client';
import { environment } from 'src/environments/environment';
import { AuthApiService, RefreshTokenResponse } from './auth-api.service';
import { LocalStorageService } from './local-storage.service';

const REFRESH_TIMEOUT_MS = 15000;

export const DELIVERY_BLOCKED_CODE = 'DELIVERY_BLOCKED';
export const DELIVERY_BLOCKED_MESSAGE =
  'Tu sesión fue invalidada porque tu cuenta de delivery fue bloqueada.';

export interface LogoutReason {
  code: string;
  message: string;
}

interface DeliverySessionState {
  token: string | null;
  refreshToken: string | null;
  cdDelivery: string | null;
  dsPhone: string | null;
  dsName: string | null;
}

export const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED';
export const SESSION_EXPIRED_MESSAGE =
  'Tu sesion expiro. Volve a iniciar sesion para continuar.';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {

  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private isForcedLogoutInProgress = false;
  private sessionState: DeliverySessionState = this.readSessionState();
  private refreshInFlight$?: Observable<string>;

  private readonly refreshSkewMs = environment.auth.refreshSkewMs;
  private readonly logEnabled = !environment.production;

  constructor(
    private router: Router,
    private nav: NavController,
    private authApiService: AuthApiService,
    private localStorageService: LocalStorageService,
  ) {
    this.restoreSession();
  }

  saveDeliveryUser(delivery: DeliveryResponseDto) {
    this.localStorageService.setDeliveryUser(delivery);
    this.localStorageService.clearLogoutReason();
    this.refreshSessionState();
  }

  clearDeliveryUser() {
    this.localStorageService.clearDeliverySession();
    this.sessionState = this.readSessionState();
    this.isAuthenticatedSubject.next(false);
  }

  getToken() {
    return this.sessionState.token;
  }

  getRefreshToken() {
    return this.sessionState.refreshToken;
  }

  isAuthenticated() {
    return this.isAuthenticatedSubject.value;
  }

  restoreSession() {
    this.refreshSessionState();
  }

  private hasValidDeliverySession() {
    const session = this.sessionState;
    return !!(session.token && session.cdDelivery);
  }

  getValidAccessTokenForRequest$(): Observable<string | null> {
    if (!this.isAuthenticated()) {
      return of(null);
    }

    if (this.refreshInFlight$) {
      return this.refreshInFlight$.pipe(map((token) => token || this.getToken()));
    }

    return this.ensureValidAccessToken$();
  }

  ensureValidAccessToken$(): Observable<string | null> {
    const token = this.getToken();
    if (!token) {
      return of(null);
    }

    if (!this.isTokenExpiringSoon(token, this.refreshSkewMs)) {
      return of(token);
    }

    this.logInfo('Access token near expiration. Performing preemptive refresh.');

    return this.refreshAccessToken$('preemptive').pipe(
      catchError((error) => {
        const resolvedError = this.resolveError(error);
        if (!this.isTransientNetworkError(resolvedError)) {
          this.logout({
            code: SESSION_EXPIRED_CODE,
            message: SESSION_EXPIRED_MESSAGE,
          });
        }
        return throwError(() => resolvedError);
      })
    );
  }

  refreshAccessToken$(reason: 'preemptive' | '401' | 'foreground' | 'online' | 'manual'): Observable<string> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    this.logInfo(`Starting refresh flow. reason=${reason}`);

    this.refreshInFlight$ = this.authApiService
      .refreshToken(refreshToken, this.getToken())
      .pipe(
        timeout(REFRESH_TIMEOUT_MS),
        map((response: RefreshTokenResponse) => this.applyRefreshedTokens(response)),
        tap(() => this.logInfo('Refresh token flow completed successfully.')),
        catchError((error) => {
          const resolvedError = this.resolveError(error);
          this.logWarn('Refresh token flow failed.', this.buildErrorLogContext(resolvedError));
          if (!this.isTransientNetworkError(resolvedError)) {
            this.logout({
              code: SESSION_EXPIRED_CODE,
              message: SESSION_EXPIRED_MESSAGE,
            });
          }
          return throwError(() => resolvedError);
        }),
        finalize(() => {
          this.refreshInFlight$ = undefined;
        }),
        shareReplay(1)
      );

    return this.refreshInFlight$;
  }

  onAppForeground$(): Observable<string | null> {
    if (!this.isAuthenticated()) {
      return of(null);
    }

    return this.ensureValidAccessToken$().pipe(
      catchError((error) => {
        const resolvedError = this.resolveError(error);
        if (this.isTransientNetworkError(resolvedError)) {
          this.logWarn('Foreground refresh deferred due to connectivity issue.');
          return of(this.getToken());
        }

        return throwError(() => resolvedError);
      })
    );
  }

  onConnectivityRestored$(): Observable<string | null> {
    if (!this.isAuthenticated()) {
      return of(null);
    }

    return this.refreshAccessToken$('online').pipe(
      map((token) => token || this.getToken()),
      catchError((error) => {
        const resolvedError = this.resolveError(error);
        if (this.isTransientNetworkError(resolvedError)) {
          return of(this.getToken());
        }
        return throwError(() => resolvedError);
      })
    );
  }

  logout(reason?: LogoutReason) {
    if (reason?.code === DELIVERY_BLOCKED_CODE && this.isForcedLogoutInProgress) {
      return;
    }

    if (reason?.code === DELIVERY_BLOCKED_CODE) {
      this.isForcedLogoutInProgress = true;
    }

    if (reason) {
      this.localStorageService.setLogoutReason(JSON.stringify(reason));
    } else {
      this.localStorageService.clearLogoutReason();
    }

    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();
    if (refreshToken) {
      this.authApiService.revokeToken(refreshToken, accessToken || undefined).subscribe({
        error: (error) => {
          this.logWarn('Token revoke failed during logout.', this.buildErrorLogContext(error));
        },
      });
    }

    this.clearDeliveryUser();
    this.nav.navigateRoot('/login');
  }

  consumeLogoutReason(): LogoutReason | null {
    const storedReason = this.localStorageService.getLogoutReason();
    this.localStorageService.clearLogoutReason();

    if (!storedReason) {
      return null;
    }

    try {
      const parsed = JSON.parse(storedReason) as LogoutReason;
      if (!parsed?.code || !parsed?.message) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private applyRefreshedTokens(response: RefreshTokenResponse): string {
    const current = this.localStorageService.getDeliverySession();
    const updated: DeliveryResponseDto = {
      cdDelivery: current.cdDelivery || undefined,
      dsPhone: current.dsPhone || undefined,
      dsName: current.dsName || undefined,
      token: response.accessToken,
      accessToken: response.accessToken,
      refreshToken: response.refreshToken || current.refreshToken || undefined,
    };

    this.localStorageService.setDeliveryUser(updated);
    this.refreshSessionState();

    return response.accessToken;
  }

  private refreshSessionState() {
    this.sessionState = this.readSessionState();
    this.isAuthenticatedSubject.next(this.hasValidDeliverySession());
  }

  private readSessionState(): DeliverySessionState {
    const session = this.localStorageService.getDeliverySession();
    return {
      token: session.token || null,
      refreshToken: session.refreshToken || null,
      cdDelivery: session.cdDelivery || null,
      dsPhone: session.dsPhone || null,
      dsName: session.dsName || null,
    };
  }

  private isTokenExpiringSoon(token: string, skewMs: number): boolean {
    const payload = this.decodeJwtPayload(token);
    if (!payload || !payload.exp) {
      return false;
    }

    const expiresAtMs = Number(payload.exp) * 1000;
    const now = Date.now();

    return now + skewMs >= expiresAtMs;
  }

  private decodeJwtPayload(token: string): any | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }

      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(normalized);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  private isTransientNetworkError(error: any): boolean {
    const resolvedError = this.resolveError(error);
    if (resolvedError instanceof TimeoutError) {
      return true;
    }
    return resolvedError?.status === 0;
  }

  private logInfo(message: string, context?: any) {
    if (!this.logEnabled) {
      return;
    }
    if (context !== undefined) {
      console.info('[Auth]', message, context);
      return;
    }
    console.info('[Auth]', message);
  }

  private logWarn(message: string, context?: any) {
    if (context !== undefined) {
      console.warn('[Auth]', message, context);
      return;
    }
    console.warn('[Auth]', message);
  }

  private resolveError(error: any): any {
    if (typeof error !== 'function') {
      return error;
    }

    try {
      return error();
    } catch {
      return error;
    }
  }

  private buildErrorLogContext(error: any) {
    const resolvedError = this.resolveError(error);
    return {
      status: resolvedError?.status ?? null,
      code: resolvedError?.error?.code ?? null,
      message: resolvedError?.error?.message || resolvedError?.message || null,
      url: resolvedError?.url ?? null,
    };
  }

}
